// M13 (docs/backlog.md, docs/build-briefs/M13-authentication-required-on-the-
// money-screen.md). Pure plain-language translation for a failed Pay or
// Close response, extracted out of ui/src/main.ts so it can be unit-tested
// directly (main.ts cannot be imported by a plain node:test file: it runs
// module-level DOM code at import time, including window.location.origin
// and mustFind calls). Mirrors ui/src/managerExceptionDisplay.ts's own
// shape (AU8): a lookup table plus one pure function, no DOM access.
//
// This is a relocation of code that already existed in main.ts, plus
// exactly one new map entry (authentication_required). No call site, no
// route, and no domain module changed as part of this move; the four
// call sites in main.ts (previously at :11369, :11519, :11588, :11870)
// import the three names below in place of the local declarations they
// used to define.

import { SERVICE_UNAVAILABLE_SENTENCE } from "./serviceUnavailable.ts";
import type { ApiErrorBody } from "./api.ts";

export interface TranslatedPayCloseError {
  plain: string;
  detail: string;
  /** PAY14 (D8): how #pay-status presents `plain`. "error" (the
   * `pay-status error` class) for every code except the few named in
   * PAY_CLOSE_STATUS_PRESENTATION below, which render in the plain
   * `pay-status` style because nothing went wrong. */
  presentation: PayCloseStatusPresentation;
}

export type PayCloseStatusPresentation = "error" | "plain";

/** PAY14 (docs/build-briefs/PAY14-order-hold-provider-recheck.md, Section
 * 8.2, D8, approved by the ui-ux-designer in round 2 of
 * design/reviews/pay14-order-hold-provider-recheck-2026-09-25.md): an
 * explicit per-code presentation for the one refusal that is not an
 * error. `hold_cleared_take_new_card` means a test (simulated) hold was
 * just set aside and Pay stopped so a new card can be taken; the rail
 * shows it in the plain style, never the error style. Every code not
 * named here presents as "error". */
export const PAY_CLOSE_STATUS_PRESENTATION: Readonly<Record<string, PayCloseStatusPresentation>> = {
  hold_cleared_take_new_card: "plain",
};

export const KNOWN_PAY_CLOSE_ERROR_MESSAGES: Record<string, (action: "pay" | "close") => string> = {
  idempotency_key_reused: () => "Payment details changed. Check the ticket before trying again.",
  // OWNSUSP1 (PRD P0.32): the location is suspended or cancelled. The server's own message names the state; this is the fallback.
  account_standing: () => "This location is suspended or cancelled. Contact Snip & Sip.",
  billing_paused: () =>
    "Subscription billing needs attention. Normal location work is paused. Ask your account owner to resolve billing.",
  // ID1-F2 (DEC-398). Required mode: the floor opens the ID sheet before Pay, so this is a race (the mode was just
  // switched, or the ticket changed under the sheet). Mapped so the screen reads plainly instead of "unexpected".
  id_check_required: () => "ID not checked. Tap Pay again to verify the ID or skip the check.",
  // RCPT1-S1 (DEC-401). A tip written on a receipt that is over the limit, or a card that cannot take a tip after the sale.
  // The Awaiting tip sheet (RCPT1-S3) says this itself; mapped now so the codes never read as "unexpected".
  receipt_tip_over_cap: () => "That tip is over the limit for this sale. Enter a smaller tip.",
  // TIPMIN1. A card charge under the provider's $0.50 minimum, refused before any provider call.
  card_minimum_charge: () => "Card payments must be at least $0.50. Take this tip in cash or choose a larger tip.",
  receipt_tip_not_supported: () => "This card cannot take a tip after the sale. Charge it with no tip.",
  lines_not_prepared: () =>
    "A drink on this ticket is not Prepared yet. Prepare every drink, then try again.",
  not_found: () => "This ticket could not be found. Go back to Open Tabs and try again.",
  // CORE4. A payment now prepares the drinks it covers through the Prepared write, which refuses a device at another
  // location than the ticket's. The payment passes the system opt-out, so it is not reachable today; mapped so the call
  // graph's reach is covered and a later change that supplies a location still reads plainly.
  // CORE4. The Prepared write refuses a drink that is already Prepared. A payment prepares only the Not Prepared drinks
  // it has locked, so it cannot hit this itself; mapped because the call graph reaches the write.
  line_already_prepared: () =>
    "A drink on this ticket was just marked Prepared. Check the ticket, then try again.",
  ticket_location_mismatch: () =>
    "This ticket belongs to a different location. Use a device set up for that location.",
  // VER33-F1 (docs/build-briefs/VER33-F1-pin-change-required-on-pay-
  // close.md). A mustChangePin session's Pay/Close/split-collect/no-tip
  // attempt is now intercepted in main.ts BEFORE this function is ever
  // called (handlePinChangeRequiredOnPayClose, called first in all four
  // failure branches, opens the existing mandatory change-PIN pad
  // instead), so this entry is never actually reached through those four
  // routes today. It exists as a defensive fallback so the client's own
  // translation table stays complete for any OTHER caller that might
  // read a Pay/Close-family error without going through this session's
  // four intercept points, and so removing the matching
  // check-pay-close-error-coverage.ts ALLOWLIST entry (the backlog row's
  // own explicit instruction) is a real removal rather than trading one
  // coverage gap for another.
  pin_change_required: () =>
    "You need a new PIN before you can do this. Open My Profile to change it.",
  order_closed: (action) =>
    action === "pay"
      ? "This ticket is already closed and cannot be paid again."
      : "This ticket is already closed.",
  // PAY31 (docs/build-briefs/PAY30-pay-held-tab.md): every ValidationError
  // in this codebase carries this one code (src/domain/errors.ts's own
  // ValidationError), so a single fixed sentence here cannot distinguish
  // a missing field from a hold-capture refusal from a stale saved card.
  // translatePayCloseError below consults INVALID_REQUEST_MESSAGE_PREFIXES
  // first, keyed on the actual ValidationError message's own fixed
  // prefix, and falls back to this generic sentence only when nothing
  // there matches (an unmapped or future ValidationError). This entry
  // itself IS that fallback; keep it last-resort and plain, never the
  // raw server text.
  invalid_request: () => "Something about this ticket could not be processed. Tell a manager.",
  // CL1, owner ruling 2026-08-28: a ticket does not close while anything
  // is still owed. The server's own message carries the exact figure, and
  // that figure is the useful part on the floor, so this entry keeps it
  // rather than replacing it with a generic sentence. The raw text is
  // already 8th-grade ("order ... still owes $13.00; Close is blocked
  // until the balance is zero. Take a payment, or apply a discount, a
  // comp, or the Walkout discount"), so only the order id in front of it
  // is trimmed.
  order_balance_outstanding: () =>
    "This ticket still has money owed. Take a payment, or apply a discount, a comp, or Walkout.",
  // Gate-found gap correction (2026-08-26): payOrder and, as of this
  // correction, closeOrder both refuse to act on an order with an
  // unresolved Pending payment attempt (src/domain/errors.ts's
  // PaymentPendingReconciliationError, the owner's "block-and-flag"
  // ruling). Before this entry, that 409 had no known-message mapping
  // here, so it fell through to the generic "Could not process the
  // payment/close the ticket" fallback below, which does not tell staff
  // WHY or that a manager needs to step in. Plain, 8th-grade language,
  // action-aware the same way order_closed already is above.
  payment_pending_reconciliation: (action) =>
    action === "close"
      ? "This tab has a payment still being confirmed. It cannot be closed until that is resolved."
      : "This tab has a payment still being confirmed. It cannot be paid again until that is resolved.",
  // PAY7 (docs/build-briefs/PAY7-pay-retry-after-terminal-change.md
  // Section 2.6): src/domain/errors.ts's PaymentRetryConflictError, a 409
  // from chargeThroughAdapter/openTabWithHold when a retry under the same
  // domain idempotency key reaches a different terminal than an earlier,
  // still-possibly-live attempt already presented the same underlying
  // intent to. Worded to actively discourage the one recovery path that
  // is UNSAFE here (Section 2.5: a reload mints a fresh key, which is
  // exactly how the double-charge risk this item closes would reopen) --
  // "do not reload this screen" is the one instruction this message
  // carries that no sibling entry in this file needs to.
  payment_retry_conflict: (action) =>
    action === "pay"
      ? "This payment is still finishing from an earlier attempt. Wait a few seconds and try again; do not reload this screen."
      : "This ticket has a payment still finishing from an earlier attempt. Wait a few seconds and try again; do not reload this screen.",
  // TAX2-S1-F3: src/domain/errors.ts's CapturedAmountMismatchError, a 409 raised AFTER the provider
  // captured an amount this payment cannot record. The card WAS charged; a manager exception is open
  // on the order. Action-independent, same sentence the server sends. No new screen.
  captured_payment_reconciliation_required: () =>
    "Payment needs reconciliation. Do not charge the card again.",
  captured_amount_mismatch: () =>
    "The card was charged for a different amount than this payment asked for, so it was not recorded. Do not charge the card again. A manager has been alerted to reconcile it.",
  // TAX2-S1-F3: CapturedAmountMismatchNotAlertedError, the same charge, but the manager exception could
  // not be written, so the sentence asks the person to tell a manager instead of claiming one was alerted.
  captured_amount_mismatch_not_alerted: () =>
    "The card was charged for a different amount than this payment asked for, so it was not recorded. Do not charge the card again. Tell a manager now so they can reconcile it.",
  // PAY32 (docs/build-briefs/PAY32-reader-busy.md): src/domain/errors.ts's
  // PaymentReaderBusyError, a 409 from chargeThroughAdapter (Pay and split
  // collect) when the card reader is processing another payment. Nothing was
  // charged and nothing is left pending, so the plain instruction is just to
  // wait and tap Pay again. Action-independent, same sentence the server sends.
  terminal_reader_busy: () =>
    "This card reader is busy with another payment. Wait for it to finish and try again.",
  // TERM6 (docs/build-briefs/TERM6-reader-offline-and-action-failed.md):
  // src/domain/errors.ts's PaymentReaderOfflineError, a 409 from
  // chargeThroughAdapter (Pay and split collect) when the card reader is offline,
  // either by the pre-flight read or Stripe's own terminal_reader_offline. Nothing
  // was charged and nothing is left pending. Action-independent, same sentence the
  // server sends.
  terminal_reader_offline: () =>
    "This card reader is offline. Nothing was charged. Check that it is powered on and connected to the internet, then try again.",
  // CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-286):
  // src/domain/errors.ts's BusinessDayNotConfirmedError, a 409 from the
  // live-money gate (src/domain/liveMoneyGate.ts) on every path that moves or
  // reserves real card money while the location's time zone and cutoff are
  // unconfirmed. Raised before any provider call, so nothing was charged. The
  // floor also shows its own sheet for this code (the line below is what stays
  // in #pay-status after OK, the wording the design fixes). Action-independent.
  business_day_not_confirmed: () =>
    "Card payments are off until a manager confirms the time zone. No card was charged.",
  // ONB1 (docs/build-briefs/ONB1-first-owner-handoff.md): src/domain/errors.ts's TaxRateNotConfirmedError, the same
  // live-money gate's refusal while the sales tax rate is unconfirmed. Raised before any provider call, so nothing
  // was charged. Action-independent.
  tax_rate_not_confirmed: () =>
    "Card payments are off until an admin confirms the sales tax rate. No card was charged.",
  // AU8 (docs/backlog.md): src/domain/errors.ts's ManagerExceptionBlocksOrderError
  // (409, assertNoOpenManagerExceptionBlocksOrder), the refusal this item's
  // own ticket banner exists to warn about BEFORE a bartender ever reaches
  // it. Before this entry that 409 had no known-message mapping here either,
  // same gap payment_pending_reconciliation closed above.
  manager_exception_blocks_order: (action) =>
    action === "close"
      ? "This tab has an open manager exception. A manager must resolve it before this ticket can be closed."
      : "This tab has an open manager exception. A manager must resolve it before you can take another payment.",
  // RBAC track: neither money.payment.take nor money.tab.close resolves
  // to approval_required under any SEEDED role template today (only
  // money.saved_card.remove/money.tab.reopen/inv.count_session.close do,
  // and those three are wired through requestApproval's own picker+PIN
  // flow, never through Pay/Close's translatePayCloseError path at all).
  // A per-user override (setUserPermissionOverride) COULD still put
  // either permission into approval_required, and that route does not
  // parse approverUserId/approverPin the way the three wired routes do,
  // so Pay/Close have no way to complete such an approval from this
  // screen. This entry only keeps that theoretical case from falling
  // through to the generic "Could not process/close" fallback with no
  // explanation at all.
  approval_required: () =>
    "A manager's approval is needed for this, and this screen cannot collect it. Ask a manager for help.",
  // M13: extractBearerToken and validateSession
  // (src/domain/users.ts) throw this code for a missing, malformed, or
  // revoked session. This entry is keyed narrowly on the literal string
  // authentication_required, so a real card decline, a provider timeout,
  // or any capture failure not yet given its own entry (a different
  // error code from src/domain/errors.ts) still falls through to the
  // unchanged generic fallback below. Worded distinctly from the
  // existing inactivity toast ("Signed out after a period of
  // inactivity. Sign in again."), since this is a different cause (a
  // missing or revoked bearer token, not a sliding-window timeout) and
  // this entry does not claim to know which one produced any given 401.
  authentication_required: (action) =>
    action === "pay"
      ? "You are signed out. Sign in again, then try this payment again."
      : "You are signed out. Sign in again, then try closing this ticket again.",
  // PAY5 (docs/backlog.md, docs/build-briefs/PAY5-no-terminal-plain-
  // message.md): src/domain/errors.ts's PaymentTerminalNotFoundError, a
  // 404 from resolveTerminalReferenceForCharge (src/domain/payments.ts)
  // when a device's remembered terminalId no longer resolves to an
  // active, tenant-owned row (never registered, wrong tenant, or
  // deactivated since this device last stored it). Before this entry that
  // 404 fell through to the generic fallback below with no explanation.
  // Names the exact remedy (account menu, Payment Terminal) at an
  // eighth-grade reading level, matching this file's own established
  // wording pattern.
  payment_terminal_not_found: (action) =>
    action === "pay"
      ? "This device's card reader is no longer set up. A manager can pick one from the account menu's Card reader item, then try this payment again."
      : "This device's card reader is no longer set up. A manager can pick one from the account menu's Card reader item, then try closing this ticket again.",
  // PAY5: src/domain/errors.ts's TerminalNotAssignedError, a 409 from
  // resolveTerminalReferenceForCharge when a non-simulated provider (every
  // production provider except the documented simulated placeholder) is
  // asked to charge with no terminalId at all -- a device that has never
  // picked a terminal. Before this entry that 409 fell through to the
  // generic fallback below, the exact defect the backlog row's own
  // 2026-09-23 stage0 live-run report names.
  // CASH1-S2: the three refusals a cash payment can meet. Plain words; the cashier's next step is in each.
  cash_tendered_below_due: () => "The cash handed over is less than the amount due. Count it again.",
  cash_not_enabled: () => "This location is not taking cash.",
  cash_drawer_not_open: () => "Open a cash drawer on this device first, then take the payment.",
  terminal_not_assigned: (action) =>
    action === "pay"
      ? "This device has no card reader set up yet. A manager can set one from the account menu's Card reader item, then try this payment again."
      : "This device has no card reader set up yet. A manager can set one from the account menu's Card reader item, then try closing this ticket again.",
  // PAY5, mapping PAY6's own code (docs/build-briefs/PAY6-terminal-
  // provider-mismatch.md): src/domain/errors.ts's TerminalProviderMismatchError,
  // a 409 when a registered terminal's own provider_name no longer
  // matches the adapter now configured to charge (a tenant's configured
  // provider changed since the terminal was registered under the old
  // one). Worded distinctly from terminal_not_assigned ("needs to be set
  // up again" vs. "has no card reader set up yet") because the
  // remedy differs in a way a bartender should not have to guess at: this
  // device HAD a terminal, it is now stale and needs to be registered
  // fresh, not picked for the first time.
  terminal_provider_mismatch: (action) =>
    action === "pay"
      ? "This device's card reader needs to be set up again. A manager can register it fresh from the account menu's Card reader item, then try this payment again."
      : "This device's card reader needs to be set up again. A manager can register it fresh from the account menu's Card reader item, then try closing this ticket again.",
  terminal_merchant_account_mismatch: () =>
    "This card reader needs to be registered again for this location's payment account. Nothing was charged. Ask a manager.",
  // TERM4 (docs/build-briefs/TERM4-card-readers-per-bar.md): src/domain/errors.ts's TerminalOtherLocationError, a
  // 409 when the reader this device remembered belongs to a different location (a tablet moved to another location, or a
  // reader registered for another location). Nothing was charged. The remedy is the same picker, which now lists only
  // this location's readers. Names no other location.
  terminal_other_location: (action) =>
    action === "pay"
      ? "That card reader belongs to a different location. Nothing was charged. Pick a reader at this location from the account menu's Card reader item, then try this payment again."
      : "That card reader belongs to a different location. Pick a reader at this location from the account menu's Card reader item, then try closing this ticket again.",
  // PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md, section 3.7; P0.23 criterion 6): the location's card
  // payments account is not enabled while connected accounts are required. One plain sentence, owner-approved
  // mockup (design/mockups/pay1-s3-floor-pay-refusal.html), shown inline under Pay. Nothing was charged. Names
  // neither an account nor a state: the person at the location can only ask a manager.
  merchant_account_not_ready: () => "Card payments are not set up for this location. Ask a manager.",
  merchant_account_tenant_context: () =>
    "This location cannot use that card payments account. Ask a manager for help.",
  merchant_account_unavailable: () =>
    "This location's card payments account is unavailable. Ask a manager for help.",
  merchant_account_keys_not_configured: () =>
    "Card payments are not set up yet. Ask the software owner for help.",
  discount_policy_conflict: () =>
    "Discount settings changed. Refresh the ticket, then try again.",
  // TERM4: src/domain/errors.ts's DeviceNotBoundError (LOC1-S2), now also reachable from Pay and split-collect
  // when a session with no bound tablet names a reader. Nothing was charged.
  device_not_bound: () =>
    "This device is not set up for a location. Nothing was charged. Ask a manager for a setup code.",
  // VER33 (docs/build-briefs/VER33-pay-close-error-coverage.md), wording
  // reviewed by the ui-ux-designer in
  // design/reviews/ver33-refusal-wording-2026-09-24.md ("New entries"
  // table). src/domain/errors.ts's GuestNotFoundError, a 404 from payOrder
  // (src/domain/payments.ts) when the per-guest Pay branch's own guestId
  // no longer matches an active guest on this order (a stale screen, or
  // the guest was never really added). Pay only. Before this entry that
  // 404 fell through to the generic fallback below with no explanation.
  guest_not_found: () =>
    "This guest is not on this ticket anymore. Refresh the ticket, then try again.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's NothingUnpaidError, a 409 from
  // payOrder when every active line (or this guest's own share of it) is
  // already covered by a captured payment -- a double-tap, a stale
  // screen, or two bartenders on the same ticket. Pay only. Worded as
  // reassuring rather than a failure, since it usually means the ticket
  // was already paid.
  // DEC-609: the platform live-money checklist applies to every location; there is no per-location confirmation.
  live_money_precondition_not_met: () =>
    "Card payments are not switched on yet. The software owner must complete the platform live-money checklist.",
  nothing_unpaid: () =>
    "There is nothing left to pay on this ticket. Refresh to see the latest balance.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's SplitPaymentInProgressError, a
  // 409 from payOrder when a whole-ticket or per-guest Pay is attempted
  // while an equal-split plan on the same tab is still actively
  // collecting shares. Pay only. Names both real remedies (finish the
  // split, or cancel it) since either genuinely clears the block.
  split_payment_in_progress: () =>
    "This tab has a split payment in progress. Finish collecting the split, or cancel it, before paying another way.",
  // SPLIT1 (docs/build-briefs/SPLIT1-pay-by-guest-and-by-amount.md section 3.2). src/domain/errors.ts's
  // AmountExceedsBalanceError, a 409 from payOrder when a By amount request is more than the ticket still owes
  // (another device paid in between, or the pad was out of date). Pay only. Nothing was charged. DRAFT wording,
  // to be reviewed by the ui-ux-designer (DEC-194).
  amount_exceeds_balance: () =>
    "That is more than the ticket still owes. Nothing was charged. Check the balance, then try again.",
  // SPLIT1 review S4. src/domain/errors.ts's PaymentLinkOutError, now also a 409 from payOrder when a By amount payment
  // is attempted while a payment link is out on the ticket (a guest may be paying the link right now). Nothing was
  // charged. DRAFT wording, to be reviewed by the ui-ux-designer (DEC-194).
  payment_link_out: () =>
    "A payment link is out for this ticket. Cancel it first. Nothing was charged.",
  // SPLIT1 review B1 and A3. src/domain/errors.ts's AmountCreditsExceedTicketError, a 409 from Pay, Close and the
  // settlement close when the ticket now totals less than was already collected by amount (a tax-rate change, a
  // discount or a price recomputing lower). Nothing was charged. Sends to a manager. DRAFT wording, to be reviewed by
  // the ui-ux-designer.
  amount_credits_exceed_ticket: () =>
    "This ticket now totals less than was already collected by amount. Nothing was charged. Get a manager.",
  // SPLIT1 (section 3.3). src/domain/errors.ts's AmountPaymentsInProgressError, a 409 from payOrder when By guest,
  // the No guest row or a named-drinks payment is attempted after a payment taken by amount (owner ruling
  // 2026-10-10, DEC-363 Q2). Pay only. Nothing was charged. DRAFT wording, to be reviewed by the ui-ux-designer.
  amount_payments_in_progress: () =>
    "A payment was already taken by amount on this ticket. Pay the rest by amount or pay the whole ticket. Nothing was charged.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's SavedCardNotFoundError, a 404
  // from payOrder when a selected saved card no longer resolves to an
  // active saved card for a guest on this order (removed since the
  // screen loaded, or never really attached). Pay only.
  saved_card_not_found: () =>
    "That saved card is not available anymore. Pick a different card, or enter a new one.",
  // VER34 (docs/build-briefs/VER34-provider-recheck-tests.md), wording
  // reviewed and approved by the ui-ux-designer in design/reviews/ver34-
  // saved-card-provider-mismatch-2026-09-24.md. src/domain/errors.ts's
  // SavedCardProviderMismatchError, a 409 from payOrder's saved-card
  // branch when the saved card's own stored provider_name no longer
  // matches the adapter now configured to charge (a provider cutover
  // left a stale vaulted card behind). Pay only, matching saved_card_
  // not_found's own scope exactly.
  saved_card_provider_mismatch: () =>
    "That saved card cannot be charged anymore. Have the guest add a new card, or use another payment method.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's HoldCaptureExceedsAuthorizedError,
  // a 409 from payOrder when the ticket's total due is more than the
  // amount currently authorized on the card hold already placed. Pay
  // only. Sends straight to a manager, matching how
  // payment_terminal_not_found and terminal_not_assigned above already
  // route to a manager rather than implying a bartender-only fix exists.
  hold_capture_exceeds_authorized: () =>
    "The total is more than the hold on file covers. Get a manager to take a second card or place a new hold.",
  // PAY14 (docs/build-briefs/PAY14-order-hold-provider-recheck.md, Section
  // 8.1, D1, approved by the ui-ux-designer in round 1 of
  // design/reviews/pay14-order-hold-provider-recheck-2026-09-25.md).
  // src/domain/errors.ts's HoldProviderMismatchError, a 409 from payOrder
  // (and collectShare) when the tab's hold was placed through a payment
  // processor this location no longer uses, and from the manager-exception
  // guard on every later tap. Pay only, rail only, no toast (PAY11).
  hold_provider_mismatch: () =>
    "This tab's card hold can't be charged anymore. Get a manager to clear it, then take a new card.",
  // PAY14 (Section 8.2, D8, approved by the ui-ux-designer in round 2).
  // src/domain/errors.ts's HoldClearedTakeNewCardError, a 409 from payOrder
  // when it found the tab's hold recorded under the simulated (test)
  // processor, set it aside (it held no money), and stopped without
  // charging. Pay only; presented in the plain style
  // (PAY_CLOSE_STATUS_PRESENTATION above), since nothing went wrong.
  hold_cleared_take_new_card: () =>
    "This tab's test hold was cleared. Tap Pay again to take a card.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's TipUnresolvedError, a 409 from
  // closeOrder (src/domain/close.ts) when the balance is zero but no tip
  // was ever recorded and nobody used the floor's explicit No Tip action
  // (A3/DEC-016). Close only. Names the exact two ways to clear it.
  tip_unresolved: () =>
    "This ticket needs a tip decision before it can close. Add a tip with a payment, or use No Tip.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/errors.ts's ShareAlreadyCollectedError, a
  // 409 from collectShare (src/domain/splitPayments.ts) when a Collect
  // tap lands on a share that was already collected (double-tap, or a
  // second device working the same split). Reached through the
  // split-collect route, which calls translatePayCloseError("pay", ...)
  // per this file's own translatePayCloseError call sites in
  // ui/src/main.ts. Worded as reassuring, mirroring nothing_unpaid above:
  // this usually means the collection already went through.
  share_already_collected: () =>
    "This share is already collected. Refresh the split screen to see the current status.",
  // VER33, wording reviewed in design/reviews/ver33-refusal-wording-
  // 2026-09-24.md. src/domain/permissions.ts's InsufficientRightsError, a
  // 403 thrown by requirePermission's own "denied" branch, called by all
  // four Pay/Close-family routes before their own domain function runs.
  // This is the ORDINARY outcome for staff whose tier cannot take a
  // payment or close a ticket, not a rare failure. "Permission" is named
  // rather than any role, matching this codebase's permission-driven
  // rule; action-aware the same two-branch shape authentication_required
  // above already uses.
  insufficient_rights: (action) =>
    action === "pay"
      ? "You don't have permission to take payments. Ask a manager for help."
      : "You don't have permission to close tickets. Ask a manager for help.",
  // PAY28 (docs/build-briefs/PAY28-money-path-lock-timeout.md).
  // src/domain/errors.ts's ServiceContentionError, a 503 raised when the
  // money-path transaction's own SET LOCAL lock_timeout or
  // statement_timeout fires (something else is holding a lock this
  // ticket needs) rather than ever silently hanging. Genuinely
  // retryable, so worded to say so plainly; nothing was charged or
  // changed, matching this codebase's own "never call a provider before
  // a throwable write" rule -- the refusal always fires before any
  // provider call on the money-path commands that can raise it.
  service_busy: () => "This ticket is busy right now. Wait a moment and try again.",
  // OPS7. The database could not be reached (503 service_unavailable); nothing was charged or changed.
  service_unavailable: () => SERVICE_UNAVAILABLE_SENTENCE,
  // TM1 (docs/backlog.md; DEC-230). src/domain/tabMerge.ts's own
  // TabMergeRefusedError, a 409 thrown for several distinct reasons (the
  // same tab twice, either tab closed or canceled, different business
  // days or locations, either tab mid-payment, already partially paid, split in
  // progress, or carrying an open walkout exception). The server's own
  // message names an order by its UUID, which is never fit for the
  // floor; this entry is the one plain, bartender-safe sentence every
  // merge refusal shows instead, independent of which specific reason
  // fired. ui/src/main.ts's handleTabMergeConfirmClick reads this
  // through this map rather than ever displaying body.message directly,
  // so a raw order id can never reach the screen.
  tab_merge_refused: () =>
    "These tabs could not be merged. One of them may be closed, mid-payment, already partly paid, " +
    "waiting on a manager, or from a different day or location. Check Open Tabs and try again.",
  // api.ts's networkErrorResult already writes a plain, staff-appropriate
  // sentence into body.message for this code ("Could not reach the
  // server. Check your connection and try again."), so it passes through
  // as both the plain and the detail text rather than getting its own
  // rewrite here.
};

// PAY31 (docs/build-briefs/PAY30-pay-held-tab.md, "Design: PAY31 fix").
// A small, closed lookup for the `invalid_request` code specifically,
// keyed on a ValidationError message's own FIXED prefix (never the full
// text verbatim, so a message that appends a dynamic value, such as an
// amount or an id, still matches on its stable leading words). Every
// message here is one `check:pay-close-error-coverage`'s own derivation
// finds reachable from the four Pay/Close-family routes, read directly
// from its literal string-literal throw site in src/domain/payments.ts
// (none interpolate card data or anything else sensitive, verified by
// reading each one). translatePayCloseError below tries this table
// FIRST for `invalid_request`, and falls back to
// KNOWN_PAY_CLOSE_ERROR_MESSAGES.invalid_request's own generic sentence
// for anything that does not match (a request-shape ValidationError this
// table did not anticipate) -- the raw server message is never shown to
// a bartender either way. Design review: the ui-ux-designer reviews each
// sentence here before merge, the same house convention every entry in
// KNOWN_PAY_CLOSE_ERROR_MESSAGES already follows.
const INVALID_REQUEST_MESSAGE_PREFIXES: ReadonlyArray<readonly [prefix: string, plain: string]> = [
  // PAY30's own finding: payOrder's hold-capture branch
  // (src/domain/payments.ts, ~line 1886) refuses `requestedOutcome`
  // together with an Active hold on purpose (a hold capture has no
  // forced-outcome test seam this slice). PAY30 stops the floor from
  // sending the field on a held tab in the first place, so this refusal
  // becomes unreachable from the ordinary Pay flow once that fix lands --
  // it stays mapped anyway, as defense in depth for a regression or an
  // unanticipated caller, per CLAUDE.md's "prefer a loud, specific
  // failure to a quiet generic one."
  [
    '"requestedOutcome" is not supported together with an active hold',
    "This tab has a card hold on it. Try Pay again; if it still won't go through, get a manager.",
  ],
  // The saved-card sibling of the same request-shape rule (payOrder,
  // ~line 1469): a stored-credential charge has no forced-outcome test
  // seam either. Not reachable from any real floor tap today (the client
  // never sends both), kept mapped for the identical defense-in-depth
  // reason as the active-hold entry above.
  [
    '"requestedOutcome" is not supported together with "savedCardId"',
    "This payment method can't be forced to a test outcome. Try Pay again, or tell a manager.",
  ],
  // The payment-link sibling (payOrder, ~line 1480): a payment-link
  // completion is not a fresh charge and has no forced-outcome seam.
  [
    '"requestedOutcome" is not supported together with "paymentLink"',
    "This payment link can't be forced to a test outcome. Refresh the ticket, then try again.",
  ],
  // payOrder, ~line 1486: a payment-link completion never coexists with
  // a saved card either.
  [
    '"paymentLink" is not supported together with "savedCardId"',
    "This ticket's payment link and saved card can't be used together. Refresh the ticket, then try again.",
  ],
  // payOrder, ~line 1460: the one required field every real client
  // always sends; reachable only from a malformed or out-of-date client.
  [
    '"taxRateBasisPoints" is required',
    "This screen sent an incomplete payment request. Refresh the ticket, then try again.",
  ],
];

function translateInvalidRequestMessage(message: string | undefined): string | undefined {
  if (message === undefined) {
    return undefined;
  }
  const match = INVALID_REQUEST_MESSAGE_PREFIXES.find(([prefix]) => message.startsWith(prefix));
  return match?.[1];
}

// T19 (docs/backlog.md, docs/build-briefs/T19-saved-cards-unknown-until-
// loaded.md). A second, narrower map for the one hold action
// (performOpenHold's POST .../orders/:orderId/hold), which has no
// pay/close action parameter equivalent to KNOWN_PAY_CLOSE_ERROR_MESSAGES'
// own signature above. Before this map, every non-2xx response from that
// route showed the raw server message verbatim
// (`Could not place a hold (${result.body.message ?? ...}).`), including
// DomainError's own developer-aimed text (confirmed live in
// docs/build-briefs/UX39-ticket-header-more-button.md's own independent-
// verification repro: "Could not place a hold (order ... already has a
// card on file; DEC-002 Section 1 requires no hold when a card is already
// chargeable directly)."). `hold_not_allowed`'s own sentence was reviewed
// and approved by the ui-ux-designer before this brief was committed
// (T19 brief, "Designer brief review before commit (DEC-194)").
//
// Deliberately narrow: only `hold_not_allowed` carries a reviewed
// sentence. A sibling code from the same route, `hold_already_exists`
// (a second hold requested while one is already active), falls through to
// the generic fallback below rather than a specifically reviewed one; the
// T19 brief names this and recommends a separate follow-up rather than
// shipping unreviewed wording for it here.
export const KNOWN_HOLD_ERROR_MESSAGES: Record<string, () => string> = {
  hold_not_allowed: () =>
    "This tab already has a card on file, so a hold is not needed. Charge that card directly at Pay when the guest is ready to close out.",
};

export function translateHoldError(
  body: Partial<ApiErrorBody> | undefined,
  status: number,
): string {
  const code = body?.error;
  const detail = body?.message ?? code ?? `status ${status}`;
  if (code === "network_error") {
    return detail;
  }
  const known = code !== undefined ? KNOWN_HOLD_ERROR_MESSAGES[code] : undefined;
  return known?.() ?? "Could not place a hold. Tell a manager if this keeps happening.";
}

export function translatePayCloseError(
  action: "pay" | "close",
  body: Partial<ApiErrorBody> | undefined,
  status: number,
): TranslatedPayCloseError {
  const code = body?.error;
  const detail = body?.message ?? code ?? `status ${status}`;
  if (code === "network_error") {
    return { plain: detail, detail, presentation: "error" };
  }
  const known = code !== undefined ? KNOWN_PAY_CLOSE_ERROR_MESSAGES[code] : undefined;
  // PAY31: `invalid_request` alone cannot tell one ValidationError from
  // another (every one of them shares this single code), so its own
  // message-prefix lookup is tried FIRST, ahead of the flat per-code
  // sentence below. The raw server text (body.message) is read only to
  // pick which reviewed sentence to show; it is never displayed itself.
  const invalidRequestPlain =
    code === "invalid_request" ? translateInvalidRequestMessage(body?.message) : undefined;
  const plain =
    invalidRequestPlain ??
    known?.(action) ??
    `Could not ${action === "pay" ? "process the payment" : "close the ticket"}. Tell a manager if this keeps happening.`;
  const presentation =
    code !== undefined && known !== undefined
      ? (PAY_CLOSE_STATUS_PRESENTATION[code] ?? "error")
      : "error";
  return { plain, detail, presentation };
}

// PAY11 (docs/build-briefs/PAY11-one-refusal-message.md Section 4, design
// confirmed by the ui-ux-designer 2026-09-24,
// design/reviews/ver33-refusal-wording-2026-09-24.md "PAY11 brief
// review"). Every Pay/Close refusal renders in exactly one place: the
// ticket rail's own status box. The toast at each of the four call sites
// (ui/src/main.ts) is dropped for every code EXCEPT one, and this function
// is the single, shared test for that one exception, so the condition
// lives once rather than as four separately-typed-out copies that could
// drift.
//
// `authentication_required` is the one code where the rail box is never
// seen: a revoked or missing session triggers a GLOBAL sign-out
// (handleSessionLost, wired off ANY 401 app-wide, not per route --
// confirmed by the designer's own reading of ui/src/main.ts's
// handleSessionLost and src/domain/permissions.ts's requirePermission,
// which every one of the four routes calls before its own domain function
// runs) and a screen switch back to the sign-in/user-picker screen BEFORE
// the failure branch that would fill the rail box ever finishes running.
// The toast is the one thing still mounted (`position: fixed`, outside the
// ticket screen) after that switch, so it stays the message for this one
// code. This is a property of the error code alone, never of which of the
// four call sites is running: PAY11's own brief first drafted this as a
// carve-out for one call site (the whole-ticket Pay button) and the
// designer's review found the mechanism reaches all four identically, so
// the fix is one shared condition, applied at every site, not a
// per-site special case.
export function shouldShowPayCloseRefusalToast(body: Partial<ApiErrorBody> | undefined): boolean {
  return body?.error === "authentication_required";
}
